<script>
  import { workspace } from '../lib/stores/workspace.js';
  import { auth } from '../lib/stores/auth.svelte.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';
  import ProcessPopup from './tech/ProcessPopup.svelte';
  import ButtonsPopup from './tech/ButtonsPopup.svelte';
  import ChoicesPopup from './tech/ChoicesPopup.svelte';

  let { onclose, oncomplete, editPageId = null } = $props();

  const COLORS = ['#d4af37','#5fb3a8','#c97bb5','#a8c878','#7ab8d4','#d48a7a','#b59bd4','#d4a86a'];

  let phase = $state('audience');
  let saving = $state(false);

  let editPagePhase = $state(false);
  let editingPage = $state(null);
  let editTitle = $state('');
  // The Insight step this page is (BLV's per-offer step log): the canvas shows its real numbers.
  const STEP_OFFERS = [['pairs', 'Memo Pairs'], ['zones', 'Zones'], ['moments', 'Moments'], ['halloween', 'Halloween Service']];
  let editStepKey = $state('');
  let stepOptions = $state([]);
  let editButtons = $state([]);
  let editProcesses = $state([]);
  let editRoutes = $state([]);
  let deleteConfirmStep = $state(0);

  $effect(() => {
    if (editPageId && !editPagePhase) {
      editPagePhase = true;
      phase = 'edit-page';
      loadEditPage();
    }
  });

  async function loadEditPage() {
    const { data: pg } = await supabase.from('bb_tech_pages').select('*').eq('id', editPageId).maybeSingle();
    if (!pg) { showToast('Page not found', 'error'); onclose?.(); return; }
    editingPage = pg;
    editTitle = pg.title;
    editStepKey = pg.step_key || '';
    loadStepOptions();
    await loadEditDeps();
  }

  async function loadEditDeps() {
    const [bRes, pRes, cRes, jRes] = await Promise.all([
      supabase.from('bb_tech_buttons').select('*').eq('page_id', editPageId).is('deleted_at', null).order('sort_order'),
      supabase.from('bb_tech_processes').select('*').eq('page_id', editPageId).is('deleted_at', null),
      supabase.from('bb_tech_choices').select('*').eq('source_page_id', editPageId).is('deleted_at', null),
      supabase.from('bb_tech_journeys').select('*').eq('workspace_id', workspace.id).is('deleted_at', null)
    ]);
    editButtons = bRes.data || [];
    editProcesses = pRes.data || [];
    const targetIds = [...new Set((cRes.data || []).map(c => c.target_page_id))];
    let targets = [];
    if (targetIds.length) {
      const r = await supabase.from('bb_tech_pages').select('id, title').in('id', targetIds);
      targets = r.data || [];
    }
    editRoutes = (cRes.data || []).map(c => ({
      ...c,
      targetTitle: targets.find(t => t.id === c.target_page_id)?.title || '?',
      journey: jRes.data?.find(j => j.id === c.journey_id)
    }));
  }

  async function loadStepOptions() {
    if (stepOptions.length) return;
    const lists = await Promise.all(STEP_OFFERS.map(([k]) => supabase.rpc('fn_offer_steps', { p_offer: k })));
    stepOptions = STEP_OFFERS.map(([k, label], i) => ({ offer: k, label, steps: lists[i].data || [] }));
  }

  async function saveStepKey() {
    const { error } = await supabase.from('bb_tech_pages').update({ step_key: editStepKey || null }).eq('id', editPageId);
    if (error) { showToast('Save failed: ' + error.message, 'error'); return; }
    showToast(editStepKey ? 'Now showing real numbers' : 'Step cleared', 'success');
  }

  async function saveTitle() {
    await supabase.from('bb_tech_pages').update({ title: editTitle.trim() }).eq('id', editPageId);
    showToast('Title saved', 'success');
  }

  async function promoteButton(btn) {
    const { data: jys } = await supabase.from('bb_tech_journeys').select('id').eq('workspace_id', workspace.id).is('deleted_at', null).limit(1);
    const jid = jys?.[0]?.id;
    if (!jid) { showToast('Build a journey first', 'error'); return; }
    const { data: newPage, error: pErr } = await supabase.from('bb_tech_pages').insert({
      workspace_id: workspace.id,
      title: btn.name
    }).select().single();
    if (pErr) { showToast('Promote failed: ' + pErr.message, 'error'); return; }
    await supabase.from('bb_tech_choices').insert({
      workspace_id: workspace.id,
      journey_id: jid,
      source_page_id: editPageId,
      source_button_id: btn.id,
      target_page_id: newPage.id
    });
    showToast(`"${btn.name}" promoted to page`, 'success');
    await loadEditDeps();
  }

  async function removeRoute(routeId) {
    await supabase.from('bb_tech_choices').update({ deleted_at: new Date().toISOString() }).eq('id', routeId);
    showToast('Route removed', 'success');
    await loadEditDeps();
  }

  async function confirmDeletePage() {
    if (deleteConfirmStep === 0) { deleteConfirmStep = 1; return; }
    if (deleteConfirmStep === 1) { deleteConfirmStep = 2; return; }
    if (deleteConfirmStep === 2) {
      await Promise.all([
        supabase.from('bb_tech_pages').update({ deleted_at: new Date().toISOString() }).eq('id', editPageId),
        supabase.from('bb_tech_buttons').update({ deleted_at: new Date().toISOString() }).eq('page_id', editPageId),
        supabase.from('bb_tech_processes').update({ deleted_at: new Date().toISOString() }).eq('page_id', editPageId),
        supabase.from('bb_tech_choices').update({ deleted_at: new Date().toISOString() }).or(`source_page_id.eq.${editPageId},target_page_id.eq.${editPageId}`)
      ]);
      showToast('Page deleted', 'success');
      oncomplete?.(null);
      onclose?.();
    }
  }

  let audiences = $state([]);
  let selectedAudienceId = $state('');
  let creatingNewAudience = $state(false);
  let newIntentText = $state('');

  let journey = $state(null);

  // Which HQ offer this journey draws (map_offers, via the workspace's HQ company). Same record HQ shows.
  let hqOffers = $state([]);
  $effect(() => { if (workspace.id) loadHqOffers(workspace.id); });
  async function loadHqOffers(wsId) {
    const { data: ws } = await supabase.from('bb_workspaces').select('partner_id').eq('id', wsId).maybeSingle();
    if (!ws?.partner_id) { hqOffers = []; return; }
    const { data } = await supabase.from('map_offers').select('id,name').eq('partner_id', ws.partner_id).neq('status', 'retired').order('rank');
    hqOffers = data || [];
  }
  async function saveJourneyOffer(offerId) {
    if (!journey?.id) return;
    const { data, error } = await supabase.from('bb_tech_journeys').update({ offer_id: offerId || null }).eq('id', journey.id).select('id');
    if (error || !data?.length) { showToast('Save failed' + (error ? ': ' + error.message : ''), 'error'); return; }
    journey = { ...journey, offer_id: offerId || null };
  }

  let existingPages = $state([]);
  let pagesLoaded = $state(false);

  let rows = $state([{ localId: crypto.randomUUID(), title: '', pageId: null, processCount: 0, buttonCount: 0, choicePicked: false }]);
  let activeRowIdx = $state(0);
  let activeField = $state('title');
  let titleSuggestions = $state([]);
  let suggestionIdx = $state(-1);

  let endPrompt = $state(false);

  let popup = $state(null);

  async function loadAudiences() {
    const { data } = await supabase
      .from('bb_audiences')
      .select('id, name, want')
      .eq('workspace_id', workspace.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    audiences = data || [];
  }

  async function loadExistingPages() {
    const { data } = await supabase
      .from('bb_tech_pages')
      .select('id, title')
      .eq('workspace_id', workspace.id)
      .is('deleted_at', null);
    existingPages = data || [];
    pagesLoaded = true;
  }

  $effect(() => { loadAudiences(); });

  async function createNewAudienceFromIntent() {
    const intent = newIntentText.trim();
    if (!intent) { showToast('Intent required', 'error'); return; }
    saving = true;
    try {
      const { data, error } = await supabase.from('bb_audiences').insert({
        workspace_id: workspace.id,
        name: intent,
        want: intent,
        status: 'draft',
        field_states: { name: 'user_filled', want: 'user_filled' },
        field_sources: { name: 'user_typed', want: 'user_typed' }
      }).select().single();
      if (error) throw error;
      audiences = [data, ...audiences];
      selectedAudienceId = data.id;
      creatingNewAudience = false;
      newIntentText = '';
    } catch (e) {
      showToast('Audience create failed: ' + e.message, 'error');
    } finally { saving = false; }
  }

  async function startBuildingRows() {
    if (!selectedAudienceId) { showToast('Pick an audience', 'error'); return; }
    saving = true;
    try {
      const colorIdx = (audiences.findIndex(a => a.id === selectedAudienceId)) % COLORS.length;
      const aud = audiences.find(a => a.id === selectedAudienceId);
      const { data, error } = await supabase.from('bb_tech_journeys').insert({
        workspace_id: workspace.id,
        audience_id: selectedAudienceId,
        name: aud?.name || 'Journey',
        color: COLORS[Math.max(0, colorIdx)],
        status: 'draft'
      }).select().single();
      if (error) throw error;
      journey = data;
      await loadExistingPages();
      phase = 'rows';
      setTimeout(() => focusRow(0, 'title'), 50);
    } catch (e) {
      showToast('Journey start failed: ' + e.message, 'error');
    } finally { saving = false; }
  }

  function updateTitleSuggestions(rowIdx) {
    const q = (rows[rowIdx].title || '').trim().toLowerCase();
    if (q.length < 1) { titleSuggestions = []; suggestionIdx = -1; return; }
    titleSuggestions = existingPages
      .filter(p => p.title.toLowerCase().includes(q) && p.title.toLowerCase() !== q)
      .slice(0, 6);
    suggestionIdx = titleSuggestions.length > 0 ? 0 : -1;
  }

  function pickSuggestion(rowIdx, page) {
    rows[rowIdx].title = page.title;
    rows[rowIdx].pageId = page.id;
    titleSuggestions = [];
    suggestionIdx = -1;
    activeField = 'process';
    focusRow(rowIdx, 'process');
  }

  async function ensurePageSaved(rowIdx) {
    const row = rows[rowIdx];
    if (row.pageId) return row.pageId;
    const title = (row.title || '').trim();
    if (!title) return null;
    const match = existingPages.find(p => p.title.toLowerCase() === title.toLowerCase());
    if (match) {
      row.pageId = match.id;
      await persistPendingChoice(rowIdx);
      return match.id;
    }
    const { data, error } = await supabase.from('bb_tech_pages').insert({
      workspace_id: workspace.id,
      title
    }).select().single();
    if (error) {
      showToast('Page save failed: ' + error.message, 'error');
      return null;
    }
    row.pageId = data.id;
    existingPages = [...existingPages, { id: data.id, title: data.title }];
    await persistPendingChoice(rowIdx);
    return data.id;
  }

  function onTitleKey(e, rowIdx) {
    if (e.key === 'ArrowDown' && titleSuggestions.length > 0) {
      e.preventDefault();
      suggestionIdx = (suggestionIdx + 1) % titleSuggestions.length;
    } else if (e.key === 'ArrowUp' && titleSuggestions.length > 0) {
      e.preventDefault();
      suggestionIdx = suggestionIdx <= 0 ? titleSuggestions.length - 1 : suggestionIdx - 1;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (titleSuggestions.length > 0 && suggestionIdx >= 0) {
        pickSuggestion(rowIdx, titleSuggestions[suggestionIdx]);
      } else if ((rows[rowIdx].title || '').trim() === '') {
        endPrompt = true;
      } else {
        ensurePageSaved(rowIdx).then(() => {
          activeField = 'process';
          focusRow(rowIdx, 'process');
        });
      }
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      ensurePageSaved(rowIdx).then(() => {
        activeField = 'process';
        focusRow(rowIdx, 'process');
      });
    } else if (e.key === 'Escape') {
      titleSuggestions = [];
    }
  }

  function onActionKey(e, rowIdx, field) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPopup(rowIdx, field);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const order = ['title','process','buttons','choices'];
      const next = order[order.indexOf(field) + 1];
      if (next) { activeField = next; focusRow(rowIdx, next); }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const order = ['title','process','buttons','choices'];
      const prev = order[order.indexOf(field) - 1];
      if (prev) { activeField = prev; focusRow(rowIdx, prev); }
    }
  }

  function focusRow(rowIdx, field) {
    activeRowIdx = rowIdx;
    activeField = field;
    setTimeout(() => {
      const el = document.querySelector(`[data-row="${rows[rowIdx]?.localId}"][data-field="${field}"]`);
      if (el) el.focus();
    }, 0);
  }

  async function openPopup(rowIdx, kind) {
    activeRowIdx = rowIdx;
    const pageId = await ensurePageSaved(rowIdx);
    if (!pageId) { showToast('Add a title first', 'error'); return; }
    if (kind === 'process') popup = 'process';
    else if (kind === 'buttons') popup = 'buttons';
    else if (kind === 'choices') popup = 'choices';
  }

  function onPopupClose() {
    popup = null;
    refreshRowCounts(activeRowIdx);
  }

  async function refreshRowCounts(rowIdx) {
    const pageId = rows[rowIdx]?.pageId;
    if (!pageId) return;
    const [bRes, pRes] = await Promise.all([
      supabase.from('bb_tech_buttons').select('id').eq('page_id', pageId).is('deleted_at', null),
      supabase.from('bb_tech_processes').select('id').eq('page_id', pageId).is('deleted_at', null)
    ]);
    rows[rowIdx].buttonCount = (bRes.data || []).length;
    rows[rowIdx].processCount = (pRes.data || []).length;
  }

  async function onChoiceMade(buttonId) {
    const sourceRow = rows[activeRowIdx];
    if (!sourceRow.pageId) return;
    const newRow = { localId: crypto.randomUUID(), title: '', pageId: null, processCount: 0, buttonCount: 0, choicePicked: false, pendingChoice: { sourcePageId: sourceRow.pageId, sourceButtonId: buttonId } };
    rows = [...rows.slice(0, activeRowIdx + 1), newRow, ...rows.slice(activeRowIdx + 1)];
    sourceRow.choicePicked = true;
    popup = null;
    setTimeout(() => focusRow(activeRowIdx + 1, 'title'), 50);
  }

  async function persistPendingChoice(rowIdx) {
    const row = rows[rowIdx];
    if (!row.pendingChoice || !row.pageId) return;
    const { sourcePageId, sourceButtonId } = row.pendingChoice;
    await supabase.from('bb_tech_choices').insert({
      workspace_id: workspace.id,
      journey_id: journey.id,
      source_page_id: sourcePageId,
      source_button_id: sourceButtonId,
      target_page_id: row.pageId
    });
    row.pendingChoice = null;
  }

  function completeJourney() {
    if (rows.filter(r => r.pageId).length === 0) {
      showToast('Add at least one page first', 'error');
      return;
    }
    const firstPageId = rows.find(r => r.pageId)?.pageId;
    supabase.from('bb_tech_journeys').update({
      starting_page_id: firstPageId,
      status: 'draft'
    }).eq('id', journey.id).then(() => {
      showToast('Journey saved', 'success');
      oncomplete?.(journey);
      onclose?.();
    });
  }

  function continueFromEnd() {
    endPrompt = false;
    const lastIdx = rows.length - 1;
    activeField = 'choices';
    focusRow(lastIdx, 'choices');
  }

  function saveDraft() {
    showToast('Draft saved', 'success');
    oncomplete?.(journey);
    onclose?.();
  }

  async function discardJourney() {
    if (!confirm('Discard everything created in this session?')) return;
    if (journey?.id) {
      await supabase.from('bb_tech_choices').update({ deleted_at: new Date().toISOString() }).eq('journey_id', journey.id);
      await supabase.from('bb_tech_journeys').update({ deleted_at: new Date().toISOString() }).eq('id', journey.id);
    }
    onclose?.();
  }
</script>

<div class="tje-overlay" onclick={onclose}>
  <div class="tje-modal" onclick={(e) => e.stopPropagation()}>
    {#if phase === 'audience'}
      <header class="tje-header">
        <h3>Whose journey is this?</h3>
        <button class="tje-x" onclick={onclose} type="button">×</button>
      </header>
      <div class="tje-body">
        {#if !creatingNewAudience}
          <p class="tje-hint">Pick the user whose journey through your product you want to map. Their intent shapes the journey.</p>
          <select class="tje-select" bind:value={selectedAudienceId}>
            <option value="">— Pick an audience —</option>
            {#each audiences as a}
              <option value={a.id}>{a.name}{a.want ? ' — ' + a.want : ''}</option>
            {/each}
          </select>
          <div class="tje-or">or</div>
          <button class="tje-secondary" onclick={() => creatingNewAudience = true} type="button">+ New intent</button>
        {:else}
          <p class="tje-hint">What's their intent? One line.</p>
          <input
            class="tje-input"
            bind:value={newIntentText}
            placeholder="e.g. Power user buying a service"
          />
          <div class="tje-row-actions">
            <button class="tje-secondary" onclick={() => { creatingNewAudience = false; newIntentText = ''; }} type="button">Cancel</button>
            <button class="tje-primary" onclick={createNewAudienceFromIntent} disabled={saving || !newIntentText.trim()} type="button">Create</button>
          </div>
        {/if}
      </div>
      <footer class="tje-footer">
        <button class="tje-secondary" onclick={onclose} type="button">Cancel</button>
        <button class="tje-primary" onclick={startBuildingRows} disabled={!selectedAudienceId || saving} type="button">
          {saving ? 'Starting…' : 'Start journey →'}
        </button>
      </footer>

    {:else}
      <header class="tje-header">
        <div class="tje-header-left">
          <span class="tje-color-dot" style="background:{journey?.color}"></span>
          <h3>{journey?.name || 'Journey'}</h3>
          {#if hqOffers.length && journey?.id}
            <select class="tje-select tje-offer" value={journey.offer_id || ''} onchange={(e) => saveJourneyOffer(e.currentTarget.value)} title="The HQ offer this journey draws">
              <option value="">Offer: none</option>
              {#each hqOffers as o (o.id)}<option value={o.id}>Offer: {o.name}</option>{/each}
            </select>
          {/if}
        </div>
        <button class="tje-x" onclick={onclose} type="button">×</button>
      </header>

      <div class="tje-rows">
        {#each rows as row, idx}
          <div class="tje-row" class:active={idx === activeRowIdx}>
            <span class="tje-row-num">{idx + 1}</span>
            <div class="tje-title-wrap">
              <input
                class="tje-title-input"
                data-row={row.localId}
                data-field="title"
                bind:value={row.title}
                oninput={() => updateTitleSuggestions(idx)}
                onkeydown={(e) => onTitleKey(e, idx)}
                onfocus={() => { activeRowIdx = idx; activeField = 'title'; updateTitleSuggestions(idx); }}
                onblur={() => setTimeout(() => { titleSuggestions = []; }, 150)}
                placeholder="Page title…"
              />
              {#if idx === activeRowIdx && activeField === 'title' && titleSuggestions.length > 0}
                <div class="tje-suggestions">
                  {#each titleSuggestions as s, sIdx}
                    <button
                      class="tje-suggestion"
                      class:active={sIdx === suggestionIdx}
                      onmousedown={() => pickSuggestion(idx, s)}
                      type="button"
                    >{s.title}</button>
                  {/each}
                </div>
              {/if}
            </div>
            <button
              class="tje-action"
              class:active={idx === activeRowIdx && activeField === 'process'}
              data-row={row.localId}
              data-field="process"
              onclick={() => openPopup(idx, 'process')}
              onkeydown={(e) => onActionKey(e, idx, 'process')}
              type="button"
            >
              Process{row.processCount > 0 ? ` (${row.processCount})` : ''}
            </button>
            <button
              class="tje-action"
              class:active={idx === activeRowIdx && activeField === 'buttons'}
              data-row={row.localId}
              data-field="buttons"
              onclick={() => openPopup(idx, 'buttons')}
              onkeydown={(e) => onActionKey(e, idx, 'buttons')}
              type="button"
            >
              Buttons{row.buttonCount > 0 ? ` (${row.buttonCount})` : ''}
            </button>
            <button
              class="tje-action"
              class:active={idx === activeRowIdx && activeField === 'choices'}
              data-row={row.localId}
              data-field="choices"
              onclick={() => openPopup(idx, 'choices')}
              onkeydown={(e) => onActionKey(e, idx, 'choices')}
              type="button"
            >
              Choices
            </button>
          </div>
        {/each}
      </div>

      {#if endPrompt}
        <div class="tje-end-prompt">
          <p>Complete journey or select button to continue?</p>
          <div class="tje-row-actions">
            <button class="tje-secondary" onclick={continueFromEnd} type="button">Continue</button>
            <button class="tje-primary" onclick={completeJourney} type="button">Complete</button>
          </div>
        </div>
      {/if}

      <footer class="tje-footer">
        <button class="tje-tertiary" onclick={discardJourney} type="button">Discard</button>
        <div class="tje-footer-spacer"></div>
        <button class="tje-secondary" onclick={saveDraft} type="button">Save draft</button>
        <button class="tje-primary" onclick={completeJourney} type="button">Complete journey</button>
      </footer>
    {:else if phase === 'edit-page'}
      <header class="tje-header">
        <h3>Edit page</h3>
        <button class="tje-x" onclick={onclose} type="button">×</button>
      </header>
      <div class="tje-body">
        <label class="tje-section-label">Title</label>
        <div class="tje-edit-row">
          <input class="tje-input" bind:value={editTitle} onblur={saveTitle} />
        </div>

        <label class="tje-section-label">Tracks step</label>
        <div class="tje-edit-row">
          <select class="tje-select" bind:value={editStepKey} onchange={saveStepKey}>
            <option value="">None</option>
            {#each stepOptions as o (o.offer)}
              <optgroup label={o.label}>
                {#each o.steps as st (st.k)}<option value="{o.offer}:{st.k}">{st.label}</option>{/each}
              </optgroup>
            {/each}
          </select>
        </div>

        <label class="tje-section-label">Buttons ({editButtons.length})</label>
        {#each editButtons as b}
          <div class="tje-edit-row">
            <span class="tje-edit-name">{b.name}</span>
            {#if b.description}<span class="tje-edit-desc">{b.description}</span>{/if}
            <button class="tje-secondary tje-promote" onclick={() => promoteButton(b)} type="button" title="Promote to its own page">↗ Page</button>
          </div>
        {/each}
        <button class="tje-secondary" onclick={() => { popup = 'buttons'; }} type="button">+ Add buttons</button>

        <label class="tje-section-label">Processes ({editProcesses.length})</label>
        {#each editProcesses as p}
          <div class="tje-edit-row">
            <span class="tje-edit-name">{p.name || 'Unnamed'}</span>
            <span class="tje-edit-desc">{(p.steps || []).length} steps</span>
          </div>
        {/each}
        <button class="tje-secondary" onclick={() => { popup = 'process'; }} type="button">+ Add process</button>

        <label class="tje-section-label">Outgoing routes ({editRoutes.length})</label>
        {#each editRoutes as r}
          <div class="tje-edit-row">
            <span class="tje-edit-color" style="background: {r.journey?.color || '#9aa0a6'}"></span>
            <span class="tje-edit-name">→ {r.targetTitle}</span>
            <span class="tje-edit-desc">in {r.journey?.name || '?'}</span>
            <button class="tje-row-x" onclick={() => removeRoute(r.id)} type="button" title="Remove route">×</button>
          </div>
        {/each}

        <div class="tje-delete-zone">
          {#if deleteConfirmStep === 0}
            <button class="tje-tertiary tje-delete-btn" onclick={confirmDeletePage} type="button">Delete this page</button>
          {:else if deleteConfirmStep === 1}
            <p class="tje-warn">This deletes the page, all its buttons, processes, and every route in or out. Cannot be undone via UI.</p>
            <div class="tje-row-actions">
              <button class="tje-secondary" onclick={() => deleteConfirmStep = 0} type="button">Cancel</button>
              <button class="tje-tertiary tje-delete-btn" onclick={confirmDeletePage} type="button">Continue</button>
            </div>
          {:else if deleteConfirmStep === 2}
            <p class="tje-warn"><strong>Final confirm.</strong> Delete this page and everything connected?</p>
            <div class="tje-row-actions">
              <button class="tje-secondary" onclick={() => deleteConfirmStep = 0} type="button">Cancel</button>
              <button class="tje-primary tje-danger" onclick={confirmDeletePage} type="button">Delete forever</button>
            </div>
          {/if}
        </div>
      </div>
      <footer class="tje-footer">
        <button class="tje-primary" onclick={onclose} type="button">Done</button>
      </footer>
    {/if}
  </div>

  {#if popup === 'process'}
    <ProcessPopup
      pageId={editPagePhase ? editPageId : rows[activeRowIdx].pageId}
      workspaceId={workspace.id}
      onclose={() => { popup = null; if (editPagePhase) loadEditDeps(); else refreshRowCounts(activeRowIdx); }}
    />
  {:else if popup === 'buttons'}
    <ButtonsPopup
      pageId={editPagePhase ? editPageId : rows[activeRowIdx].pageId}
      workspaceId={workspace.id}
      onclose={() => { popup = null; if (editPagePhase) loadEditDeps(); else refreshRowCounts(activeRowIdx); }}
    />
  {:else if popup === 'choices'}
    <ChoicesPopup
      pageId={rows[activeRowIdx].pageId}
      workspaceId={workspace.id}
      onpick={(buttonId) => onChoiceMade(buttonId)}
      onclose={onPopupClose}
    />
  {/if}
</div>

<style>
  .tje-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200;
  }
  .tje-modal {
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 14px;
    width: 720px;
    max-width: 95vw;
    max-height: 88vh;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 60px rgba(0,0,0,0.7);
  }
  .tje-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .tje-header-left { display: flex; gap: 10px; align-items: center; }
  .tje-color-dot { width: 12px; height: 12px; border-radius: 50%; }
  .tje-header h3 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 18px; color: #f0efe9; }
  .tje-x {
    background: rgba(255,255,255,0.06); color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 28px; height: 28px; border-radius: 50%;
    cursor: pointer; font-size: 16px;
  }
  .tje-body {
    padding: 18px;
    flex: 1; overflow-y: auto;
  }
  .tje-offer { width: auto; max-width: 220px; margin-left: 10px; padding: 4px 8px; font-size: 12px; }
  .tje-hint {
    color: #9aa0a6;
    font-size: 13px;
    margin: 0 0 12px;
  }
  .tje-select, .tje-input {
    width: 100%;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
  }
  .tje-select:focus, .tje-input:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .tje-or {
    text-align: center;
    color: #6a6a6e;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    margin: 14px 0;
  }
  .tje-rows {
    flex: 1;
    overflow-y: auto;
    padding: 14px 18px;
    display: flex; flex-direction: column; gap: 6px;
  }
  .tje-row {
    display: flex; gap: 6px; align-items: center;
    padding: 8px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    transition: border-color 0.15s ease;
  }
  .tje-row.active { border-color: rgba(212,175,55,0.35); }
  .tje-row-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; color: #9aa0a6;
    width: 20px;
  }
  .tje-title-wrap { flex: 1; position: relative; }
  .tje-title-input {
    width: 100%;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f4f6;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 13px;
  }
  .tje-title-input:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .tje-suggestions {
    position: absolute;
    top: calc(100% + 2px);
    left: 0; right: 0;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 5;
    box-shadow: 0 6px 18px rgba(0,0,0,0.5);
  }
  .tje-suggestion {
    display: block; width: 100%;
    background: none; border: none;
    color: #d8d8dc;
    padding: 7px 12px;
    text-align: left;
    cursor: pointer;
    font-size: 12px;
  }
  .tje-suggestion.active, .tje-suggestion:hover {
    background: rgba(212,175,55,0.12);
    color: #d4af37;
  }
  .tje-action {
    background: rgba(255,255,255,0.04);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.08);
    padding: 7px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .tje-action:hover, .tje-action.active {
    background: rgba(212,175,55,0.12);
    border-color: rgba(212,175,55,0.35);
    color: #d4af37;
  }
  .tje-end-prompt {
    margin: 8px 18px;
    padding: 12px 14px;
    background: rgba(212,175,55,0.08);
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 10px;
  }
  .tje-end-prompt p {
    margin: 0 0 8px;
    color: #f0efe9;
    font-size: 13px;
  }
  .tje-row-actions {
    display: flex; gap: 8px; justify-content: flex-end;
  }
  .tje-footer {
    display: flex; gap: 8px; align-items: center;
    padding: 12px 18px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .tje-footer-spacer { flex: 1; }
  .tje-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
  }
  .tje-primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .tje-secondary {
    background: rgba(255,255,255,0.05);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 8px 14px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
  }
  .tje-tertiary {
    background: none;
    color: #9aa0a6;
    border: none;
    padding: 8px 4px;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
  }
  .tje-tertiary:hover { color: #d48484; }
  .tje-section-label {
    display: block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase;
    color: #9aa0a6;
    margin: 14px 0 6px;
  }
  .tje-edit-row {
    display: flex; gap: 8px; align-items: center;
    padding: 6px 10px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    margin-bottom: 4px;
  }
  .tje-edit-name { color: #f0efe9; font-size: 13px; flex: 1; }
  .tje-edit-desc { color: #9aa0a6; font-size: 11px; font-style: italic; }
  .tje-edit-color { width: 8px; height: 8px; border-radius: 50%; }
  .tje-promote {
    font-size: 10px;
    padding: 4px 8px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.8px;
  }
  .tje-row-x {
    background: rgba(255,255,255,0.04); color: #9aa0a6;
    border: 1px solid rgba(255,255,255,0.08);
    width: 22px; height: 22px; border-radius: 50%;
    cursor: pointer; font-size: 11px; line-height: 1;
  }
  .tje-row-x:hover { background: rgba(220,100,100,0.15); color: #d48484; }
  .tje-delete-zone {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid rgba(220,100,100,0.15);
  }
  .tje-delete-btn { color: #d48484; }
  .tje-delete-btn:hover { color: #f4a4a4; }
  .tje-warn {
    color: #f0efe9;
    background: rgba(220,100,100,0.08);
    border: 1px solid rgba(220,100,100,0.2);
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 12px;
    margin: 0 0 8px;
  }
  .tje-danger {
    background: linear-gradient(180deg, #d48484, #b46464);
  }
</style>
